import { useMemo } from 'react'
import {
  CalendarViewContextState,
  useCalendarViewContextStateValue,
} from '../../context'
import { format } from 'date-fns'
import { WorkerCardCell } from './WorkerCardCell'

import { Box, Flex, Text } from '@workwhile/ui'
import { WorkerShiftCell } from './WorkerShiftCell'
import { groupBy } from 'ramda'
import { CalendarViewListing } from 'pages/AssignmentDetailPage/types'
import { getShiftDate } from 'pages/AssignmentDetailPage/utils/getShiftDate'
import { BaseWorkerCardCell } from './BaseWorkerCardCell'

type WorkerRowProps = {
  worker?: CalendarViewContextState['state']['workers'][number]
  shiftsByDay:
    | CalendarViewContextState['state']['shiftsByDay']
    | Partial<Record<string /** yyyy-MM-dd */, CalendarViewListing['shifts']>>
  rowIndex?: number
}

const WorkerRow = ({ worker, shiftsByDay, rowIndex }: WorkerRowProps) => {
  const {
    state: { daysOfWeek, shiftsById },
  } = useCalendarViewContextStateValue()

  const workerCells = daysOfWeek.map((day) => {
    const dateString = format(day, 'yyyy-MM-dd')

    const shiftCells =
      shiftsByDay[dateString]?.map(({ id }) => {
        const shift = shiftsById[id]
        const work = worker && shift?.workByWorker?.[worker.id]

        // Show a "Finding Worker" pill if:
        // 1. The shift has open spots available AND
        // 2. This row's position (rowIndex) is within the number of open spots
        // For example: if a shift has 2 open spots, only show pills in rows 0 and 1.
        // This allows us to display the correct unassigned headcount per shift,
        // especially if each shift has a different number of open spots.
        return (!worker &&
          shift.numOpenSpots > 0 &&
          rowIndex !== undefined &&
          rowIndex < shift.numOpenSpots) ||
          work ? (
          <WorkerShiftCell
            key={`shift-${dateString}-${shift.id}`}
            work={work}
            shift={shiftsById[shift.id]}
          />
        ) : (
          <Box key={`shift-${dateString}-placeholder`} as="td" p={3} />
        )
      }) || []

    return (
      <Box as="td" verticalAlign="top" key={`shifts-${dateString}`}>
        <Flex
          display="flex"
          flexDirection="column"
          p={2}
          gap={3}
          alignItems="flex-start"
        >
          {shiftCells}
        </Flex>
      </Box>
    )
  })

  return (
    <tr>
      {worker ? (
        <WorkerCardCell worker={worker} />
      ) : (
        <BaseWorkerCardCell
          title={
            <Text fontSize={0} color="lightText" textAlign="left">
              Finding worker ...
            </Text>
          }
        />
      )}
      {workerCells}
    </tr>
  )
}

export const WorkerRows = () => {
  const {
    state: { workers, listings, shiftsByDay, shiftsById, workerFilter },
  } = useCalendarViewContextStateValue()

  const workerRows = useMemo(() => {
    if (!Object.keys(shiftsByDay).length) return null

    const rows = workers.map((worker) => {
      return (
        <WorkerRow
          key={`workers-${worker.id}`}
          worker={worker}
          shiftsByDay={shiftsByDay}
        />
      )
    })
    return rows
  }, [workers, shiftsByDay])

  const slotRows = useMemo(() => {
    if (!Object.keys(shiftsByDay).length) return null

    return listings
      .filter((listing) =>
        listing.shifts.some(
          ({ id }) => shiftsById[Number(id)].numOpenSpots !== 0
        )
      )
      .map((listing) => {
        const shifts = groupBy(
          ({ id }) => getShiftDate(shiftsById[Number(id)]),
          listing.shifts
        )

        return new Array(listing.numWorkersPending)
          .fill(null)
          .map((_, index) => {
            return (
              <WorkerRow
                key={`slot-${listing.id}-${index}`}
                shiftsByDay={shifts}
                rowIndex={index}
              />
            )
          })
      })
  }, [listings, shiftsById, shiftsByDay])

  if (!Object.keys(shiftsByDay).length) return null

  return (
    <>
      {workerRows}
      {workerFilter !== 'unscheduled' && slotRows}
    </>
  )
}
